import { Component, HostListener, ElementRef, Input, ChangeDetectorRef } from "@angular/core";
import * as Service from "./bfx-base-blotter-columnselector.service";
import { GridOptions, ColDef } from "ag-grid-community/main";
import { trigger, state, animate, transition, style, AnimationEvent } from "@angular/animations";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  NATIVE_DESKTOP_NO_DROP_SHADOW,
  NativeDesktopSupport,
} from "../bfx-native-desktop/native-desktop-support";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
import { BfxColDef } from "../../shared/cubiq-services/model/TableDefinition";

const LOCALE = new Intl.NumberFormat().resolvedOptions().locale;

const INSENSITIVE_COLLATOR = new Intl.Collator(LOCALE, {
  numeric: true,
  sensitivity: "base",
});

@Component({
  selector: "bfx-blotter-column-selector",
  host: {
    "(document:mousedown)": "handleMouseDown($event)",
  },
  templateUrl: "./bfx-base-blotter-columnselector.html",
  animations: [
    trigger("showHideMenu", [
      state("show", style({ opacity: 1 })),
      state("hide", style({ opacity: 0 })),
      transition("show => hide", animate("100ms ease-in")),
      transition("hide => show", animate("100ms ease-out")),
    ]),
  ],
  standalone: false,
})
export class BfxBaseBlotterColumnSelector {
  public id: string;
  public isNativeDesktop = false;
  public position: any = { x: 0, y: 0 };
  public gridOptions: GridOptions;
  public gridColumnDef: any = [];
  public elementRef;
  protected checked = false;
  private mousedownListener: () => void;
  private ownerDocument = null;
  private window: NativeDesktopWindow = null;
  private _show: boolean = false;
  private nativeDesktopElement: Element = null;

  @Input() public currentLadderBlotter: any;

  constructor(
    protected selectorService: Service.BfxBaseBlotterColumnSelectorService,
    elementRef: ElementRef,
    protected cd: ChangeDetectorRef,
  ) {
    selectorService.showSelectorSubject.subscribe(this.handleShowHideEvent.bind(this));
    this.elementRef = elementRef;
    this.isNativeDesktop = NativeDesktopSupport.isNativeDesktopContainer();
  }

  public showMenuAt(x: number, y: number): void {
    if (!x || !y) return;

    this.position.x = x;
    this.position.y = y;
    this.show = true;
  }

  private addNativeListeners() {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      const listenerFn = this.handleMouseDown.bind(this);
      const ownerDocument = (this.ownerDocument = (
        this.elementRef.nativeElement as Element
      ).ownerDocument);
      if (typeof this.mousedownListener === "function") {
        this.mousedownListener();
      }
      this.mousedownListener = () => {
        ownerDocument.removeEventListener("mousedown", listenerFn);
      };
      ownerDocument.addEventListener("mousedown", listenerFn);
    }
  }

  private removeNativeListeners() {
    if (typeof this.mousedownListener === "function") {
      this.mousedownListener();
    }

    this.removeResizeObserver();
  }

  private removeResizeObserver() {
    if (this.nativeDesktopElement != null) {
      NativeDesktopSupport.resizeObserver.unobserve(this.nativeDesktopElement);
    }
  }

  ngOnDestroy(): void {
    this.removeNativeListeners();

    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    if (this.window) {
      this.window.close();
    }
  }

  public get show(): boolean {
    return this._show;
  }

  public set show(show: boolean) {
    this._show = show;
    this.addNativeListeners();
    this.cd.detectChanges();
  }

  public hideMenu(): boolean {
    this._show = false;
    this.removeNativeListeners();

    if (this.window) {
      this.window.hide();
    }

    return false;
  }

  /**
   * Works on desktop
   * Could be optimized, since on desktop it triggers on any keyboard event even if the component is not active (displayed)
   */
  @HostListener("document:keyup", ["$event"])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.show) {
      this.hideMenu();
    }
  }

  protected getColDefHeaderName(c: ColDef): string {
    return typeof c.headerValueGetter == "function"
      ? c.headerValueGetter(
          Object.assign({}, this.gridOptions, {
            colDef: c,
          }),
        )
      : c.headerName != undefined
        ? c.headerName
        : "";
  }
  protected compareColDef(a: ColDef, b: ColDef): number {
    let headerNameA = this.getColDefHeaderName(a).toLowerCase();
    let headerNameB = this.getColDefHeaderName(b).toLowerCase();

    return headerNameA < headerNameB ? -1 : headerNameA > headerNameB ? 1 : 0;
  }

  protected sortFilteredColumnNames(filteredDefs: ColDef[]): ColDef[] {
    const arr = filteredDefs.map<[string, ColDef]>((a) => [this.getColDefHeaderName(a), a]);
    const map = new Map(arr);
    const indexes = [...map.keys()].sort(INSENSITIVE_COLLATOR.compare);

    return indexes.map((a) => map.get(a));
  }

  protected handleShowHideEvent(event: Service.BfxLadderBlotterColumnSelectorEvent) {
    try {
      this.onShowHideEvent(event);
    } catch (e) {
      logger.out.error(`${this.constructor.name}: Error on show.hide event of column selector`, e);
    }
  }

  private onShowHideEvent(event: Service.BfxLadderBlotterColumnSelectorEvent) {
    if (this.currentLadderBlotter !== event.ladderBlotter) {
      return;
    }

    if (event.eventType == Service.BfxLadderBlotterColumnSelectorEventType.SELECTOR_SHOW) {
      this.gridOptions = event.gridOptions;
      if (this.gridOptions) {
        let columnDefinitionList = this.gridOptions.columnDefs;
        let filteredDefs: ColDef[] = columnDefinitionList.filter(
          (col) => col["omitFromPicker"] !== true,
        );
        filteredDefs = this.sortFilteredColumnNames(filteredDefs);
        this.gridColumnDef = this.buildArr(filteredDefs);
      }
    }

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.handleNativeDesktopEvent(event);
      return;
    }

    switch (event.eventType) {
      case Service.BfxLadderBlotterColumnSelectorEventType.SELECTOR_HIDE:
        this.hideMenu();
        break;
      case Service.BfxLadderBlotterColumnSelectorEventType.SELECTOR_SHOW:
        if (this.gridOptions) {
          // Make x,y relative to current display container
          const viewElement = this.currentLadderBlotter?.blotterView?.nativeElement;
          const offsets = BFXDomHelper.getParentContainerOffsets(viewElement);
          this.showMenuAt(event.x - offsets.left, event.y - offsets.top);
        }
        break;
      default:
        break;
    }
  }

  protected handleNativeDesktopEvent(event: Service.BfxLadderBlotterColumnSelectorEvent) {
    if (this.window == null) {
      let windowName: string = "column-selector-" + Math.random();

      if (
        this.elementRef != null &&
        this.elementRef.nativeElement != null &&
        this.elementRef.nativeElement.firstElementChild != null
      ) {
        NativeDesktopSupport.openWindow(
          windowName,
          WindowType.Utility,
          false,
          null,
          ((window: NativeDesktopWindow) => {
            try {
              let element = this.elementRef.nativeElement.firstElementChild;

              if (element != null && element.style != null) {
                element.style.top = "0px";
                element.style.right = "0px";
                element.style.opacity = "1";
                element.style.position = "unset";
              }

              if (element != null) {
                this.nativeDesktopElement = element;
                this.window = window;
                this.window.getNativeWindow().document.body.appendChild(element);
                this.window["beforeClose"] = (() => {
                  if (this.window != null) {
                    this.window.hide();
                  }
                  return false;
                }).bind(this);
                this.handleNativeDesktopEvent(event);
              } else {
                window.close();
              }
            } catch (e) {
              logger.out.warn(
                "Failed to obtain first child element when opening column selector: " + e,
              );
              this.window = null;
              window.close();
            }
          }).bind(this),
        );
      }

      return;
    }

    switch (event.eventType) {
      case Service.BfxLadderBlotterColumnSelectorEventType.SELECTOR_HIDE:
        this.window.setParentWindow(null);
        this.window.hide();
        break;
      case Service.BfxLadderBlotterColumnSelectorEventType.SELECTOR_SHOW:
        this.gridOptions = event.gridOptions;

        if (this.gridOptions) {
          let parentWindow = NativeDesktopSupport.getWindow(this.elementRef.nativeElement);

          if (parentWindow != null) {
            this.window.setParentWindow(parentWindow);
          }

          let element: Element = this.window?.getNativeWindow()?.document?.body?.firstElementChild;

          if (element == null) {
            try {
              if (
                this.elementRef != null &&
                this.elementRef.nativeElement != null &&
                this.elementRef.nativeElement.firstElementChild != null
              ) {
                element = this.elementRef.nativeElement.firstElementChild;
              }

              if (element == null) {
                element = this.nativeDesktopElement;
              }

              if (element != null && this.window != null) {
                let childElement: any = element;
                if (childElement != null && childElement.style != null) {
                  childElement.style.top = "0px";
                  childElement.style.right = "0px";
                  childElement.style.opacity = "1";
                  childElement.style.position = "unset";
                } else {
                  logger.out.warn(
                    `${this.constructor.name}: Could not find column ` +
                      `selector element to attach to window.`,
                  );
                }

                if (element != null) {
                  this.window.getNativeWindow().document.body.appendChild(element);
                }
              }
            } catch (e) {
              logger.out.error(
                `${this.constructor.name}: Failed to reattach ` +
                  `column selector element to window`,
                e,
              );
            }
          }

          if (element != null) {
            this.show = true;
            let x: number = event.x * NativeDesktopSupport.zoomLevel;
            let y: number = event.y * NativeDesktopSupport.zoomLevel;

            element["style"].top = "0px";
            element["style"].right = "0px";

            let innerElementHeight = element["offsetHeight"];
            let innerElementWidth = element["offsetWidth"];
            const width = innerElementWidth * NativeDesktopSupport.zoomLevel;
            const height = innerElementHeight * NativeDesktopSupport.zoomLevel;

            NativeDesktopSupport.getUtilityWindowLocation(x, y, width, height).then(
              ({ x: newX, y: newY }) => {
                logger.out.info(`showing At (x:${newX}, y:${newY})`);
                this.removeResizeObserver();
                element.classList.add(NATIVE_DESKTOP_NO_DROP_SHADOW);
                NativeDesktopSupport.attachResizeObserver(element, this.window);
                this.window.setBounds(newX, newY, width, height).then(() => {
                  this.window.show();
                });
              },
            );
          } else {
            logger.out.warn(
              `${this.constructor.name}: Failed to find the ` +
                `column selector element to show in a native window.`,
            );
            this.window = null;
          }
        }
        break;
      default:
        break;
    }
  }

  protected buildArr(theArr: any[]): any[] {
    if (!theArr) return;
    let rowNumbers = 0;
    let arrOfarr = [];
    for (let i = 0; i < theArr.length; i += 4) {
      rowNumbers++;
    }
    for (let i = 0; i < rowNumbers; i++) {
      let row = [];
      let counter = 0;
      for (let x = 0; x < 4; x++) {
        let value = theArr[i + counter];
        if (!value) {
          break;
        }
        row.push(value);
        counter += rowNumbers;
      }
      arrOfarr.push(row);
    }
    return arrOfarr;
  }

  public columnSelected(_, colDef: BfxColDef) {
    if (!colDef || colDef.disableInPicker) return;
    let colId = colDef?.field ?? colDef?.colId;
    let column = this?.gridOptions?.columnApi?.getColumn(colId);
    let visible: boolean = column?.isVisible();

    if (typeof visible === "boolean") {
      colDef.hide = visible;
      colDef.visible = !visible;
      this.gridOptions.columnApi.setColumnVisible(column, !visible);
      this.currentLadderBlotter?.onSetColumnVisible?.(colDef);
    }
  }

  public showHideMenuStarted($event: AnimationEvent, selectorRef: any) {
    if (`show` === $event.toState) {
      let height: number = selectorRef.offsetHeight;
      let width: number = selectorRef.offsetWidth;
      let maxHeight: number = window.innerHeight;
      let maxWidth: number = window.innerWidth;
      let targetWindowWidth: number = this.elementRef.nativeElement.ownerDocument.body.offsetWidth;
      if (!height || !width || !maxHeight || !maxWidth || !targetWindowWidth) return;
      if (height + this.position.y > maxHeight) {
        this.position.y = maxHeight - height;
      }

      if (targetWindowWidth > maxWidth) {
        this.position.x = maxWidth - width;
      } else if (width + this.position.x >= targetWindowWidth) {
        this.position.x = targetWindowWidth - width;
      }
    }
  }

  public handleMouseDown(event) {
    let clickedComponent = event.target;
    if (!clickedComponent) return;
    let inside = false;
    do {
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);
    if (!inside) {
      if (this.show) {
        this.hideMenu();
      }
    }
  }
}
